import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { remSize } from '../../../theme';

const NotFoundMain = styled.main`
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${remSize(12)};
  text-align: center;
  padding: ${remSize(24)};
`;

/**
 * Shown when a sketch cannot be loaded — it is Private and the viewer is not the owner, or it does
 * not exist. (Firestore denies the read, so no private data is ever exposed.) This replaces the
 * silent blank default editor that used to render at such URLs. i18n keys fall back to the literal
 * string, so no new locale entries are required.
 */
const SketchNotFound = () => {
  const { t } = useTranslation();
  const authenticated = useSelector((state) => state.user.authenticated);
  // A share link carries ?token=… — if a signed-out visitor opens one, the load fails on auth, not
  // because the sketch is private. Prompt them to sign in instead of "doesn't exist".
  const hasShareToken =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('token');

  if (hasShareToken && !authenticated) {
    return (
      <NotFoundMain>
        <Helmet>
          <title>{t('SketchNotFound.SignInTitle', 'Sign in to view')}</title>
        </Helmet>
        <h1>
          {t(
            'SketchNotFound.SignInHeading',
            'Sign in to open this shared sketch'
          )}
        </h1>
        <p>
          {t(
            'SketchNotFound.SignInMessage',
            'This sketch was shared with a link. Sign in to your account to open it.'
          )}
        </p>
        <Link to="/login">{t('SketchNotFound.SignInLink', 'Sign in')}</Link>
      </NotFoundMain>
    );
  }

  return (
    <NotFoundMain>
      <Helmet>
        <title>{t('SketchNotFound.Title', 'Sketch not found')}</title>
      </Helmet>
      <h1>
        {t('SketchNotFound.Heading', 'This sketch is private or doesn’t exist')}
      </h1>
      <p>
        {t(
          'SketchNotFound.Message',
          'It may have been set to private by its owner, or the link is incorrect.'
        )}
      </p>
      <Link to="/">{t('SketchNotFound.HomeLink', 'Go to the editor')}</Link>
    </NotFoundMain>
  );
};

export default SketchNotFound;
